import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiYoutube, FiInstagram, FiFacebook, FiExternalLink } = FiIcons;

// Custom TikTok icon since react-icons/fi doesn't have it
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Pinterest icon
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const LinksPage = () => {
  const links = [
    {
      label: 'Visit Ninjacopia',
      href: 'https://ninjacopia.com',
      icon: FiGlobe,
      primary: true,
    },
    {
      label: 'NinjaTrader',
      href: 'https://ninjatraderdomesticvendor.sjv.io/Dyjebd',
      icon: FiExternalLink,
    },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/people/NinjaCopiacom/61586327452809/',
      icon: FiFacebook,
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/helloninjacopia/',
      icon: FiInstagram,
    },
    {
      label: 'TikTok',
      href: 'https://www.tiktok.com/@helloninjacopia',
      icon: TikTokIcon,
    },
    {
      label: 'YouTube',
      href: 'https://www.youtube.com/@NinjaCopia-com',
      icon: FiYoutube,
    },
    {
      label: 'Pinterest',
      href: 'https://ca.pinterest.com/ninjacopia/',
      icon: PinterestIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20">
      <SEO
        title="Links | Ninjacopia"
        description="Connect with Ninjacopia - Professional multi-account trade copying for NinjaTrader."
      />

      <div className="max-w-md mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/android-chrome-192x192.png"
              alt="Ninjacopia"
              className="w-16 h-16"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Ninjacopia
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Tools for serious prop traders
          </p>
        </div>

        {/* Links Section */}
        <div className="space-y-3 mb-12">
          {links.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center w-full px-6 py-4 rounded-lg font-medium transition-all duration-200 ${
                link.primary
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md'
              }`}
            >
              <SafeIcon icon={link.icon} className="w-5 h-5 mr-3" />
              {link.label}
            </a>
          ))}
        </div>

        {/* Risk Disclosure Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
          <div className="text-[11px] text-gray-500 dark:text-gray-500 leading-relaxed space-y-4">
            <div>
              <h3 className="font-semibold text-gray-600 dark:text-gray-400 mb-1">Risk Disclosure</h3>
              <p>
                Futures and forex trading contains substantial risk and is not for every investor. An investor could potentially lose all or more than the initial investment. Risk capital is money that can be lost without jeopardizing one's financial security or lifestyle. Only risk capital should be used for trading and only those with sufficient risk capital should consider trading. Past performance is not necessarily indicative of future results.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-600 dark:text-gray-400 mb-1">Hypothetical Performance Disclaimer</h3>
              <p>
                Hypothetical performance results have many inherent limitations, some of which are described below. No representation is being made that any account will or is likely to achieve profits or losses similar to those shown; in fact, there are frequently sharp differences between hypothetical performance results and the actual results subsequently achieved by any particular trading program. One of the limitations of hypothetical performance results is that they are generally prepared with the benefit of hindsight. In addition, hypothetical trading does not involve financial risk, and no hypothetical trading record can completely account for the impact of financial risk of actual trading. For example, the ability to withstand losses or to adhere to a particular trading program despite trading losses are material points that can also adversely affect actual trading results. There are numerous other factors related to the markets in general or to the implementation of any specific trading program that cannot be fully accounted for in the preparation of hypothetical performance results and all which can adversely affect trading results.
              </p>
            </div>

            <div className="pt-2">
              <Link
                to="/risk-disclosure"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Full Risk Disclosure →
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-8 text-[10px] text-gray-400 dark:text-gray-600">
          © {new Date().getFullYear()} Ninjacopia. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default LinksPage;
